import { register } from 'ol/proj/proj4';
import proj4 from 'proj4';
import { Box2, Camera, WebGLRenderer } from 'three';

import { updateWmsAttributions } from '../../react/components/wms-map/update-wms-attributions';
import { getProj4Definition } from '../../utils/proj4/projections';
import { generateHash } from '../../utils/text/text';
import { isDefined } from '../../utils/variables';
import { CoordinateManager } from '../coordinate-manager';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { WmsTexture } from './wms-texture';

export enum WmsToolLayerItem {
  None,
  Road,
  Satellite,
}

function getOpenlayerCompatibleProj4Definition(projection?: string) {
  const definition = getProj4Definition(
    projection ?? CoordinateManager.getInstance().getProjectProjection(),
  );

  // Return oblique mercator projections as is.
  if (definition.includes('+proj=somerc') || definition.includes('+proj=omerc')) {
    return definition;
  }

  const excludeX0Y0 = (item: string) => !item.startsWith('+x_0=') && !item.startsWith('+y_0=');

  /**
   * Remove false easting and false northing from the definition if it exists.
   * This needs to be done because OpenLayer seems to break when using some projections
   * that have very high false easting values.
   */
  return definition.split(' ').filter(excludeX0Y0).join(' ');
}

/**
 * Registers the current proj4 definitions to OpenLayer so they may be used.
 * This operation needs to be done every time proj4.defs is updated.
 *
 * NOTE: Once a definition has been defined it seems like it can not be changed.
 * There are several methods that makes it look like it's possible like
 * unregister, clearAllProjections etc. But they do not seem to work.
 */
export function refreshWmsViewerProjection(projection?: string) {
  const definition = getOpenlayerCompatibleProj4Definition(projection);

  const name = generateHash(definition);

  // Register the definition as the current WMS projection.
  proj4.defs('wms', definition);

  // Register a new proj4 definition with a unique name.
  proj4.defs(name, definition);
  register(proj4);
  return name;
}

export class WmsState {
  private static _instance: WmsState;
  static get instance() {
    if (!this._instance) {
      this._instance = new WmsState();
    }
    return this._instance;
  }

  viewerProjection?: string;
  attributions: string[] = [];
  private _selectedLayerItem: WmsToolLayerItem = WmsToolLayerItem.None;

  get selectedLayerItem(): WmsToolLayerItem {
    return this._selectedLayerItem;
  }
  set selectedLayerItem(selectedLayerItem: WmsToolLayerItem) {
    this._selectedLayerItem = selectedLayerItem;
    this.wmsMapEnabled = selectedLayerItem !== WmsToolLayerItem.None;
    publish(SubscriptionTopic.OnSkyViewWmsLayerChanged, { layerItem: selectedLayerItem });
  }

  private wmsTexture?: WmsTexture;

  private _wmsMapEnabled: boolean = false;

  set wmsMapEnabled(state: boolean) {
    this._wmsMapEnabled = state;

    if (!this.wmsMapEnabled) {
      updateWmsAttributions(null);
    }

    publish(SubscriptionTopic.OnSkyViewWmsMapToggled);
  }

  get wmsMapEnabled() {
    return this._wmsMapEnabled;
  }

  initialize() {
    this.wmsTexture = new WmsTexture();
    this.attributions = [];
    this._wmsMapEnabled = false;
    this._selectedLayerItem = WmsToolLayerItem.None;
    this.viewerProjection = refreshWmsViewerProjection();
  }

  setCanvas(canvas: HTMLCanvasElement | undefined, extents: Box2) {
    this.wmsTexture?.setCanvas(canvas, extents);
  }

  getQuad() {
    return this.wmsTexture?.getScene();
  }

  render(renderer: WebGLRenderer, camera: Camera) {
    const quad = this.getQuad();
    if (!isDefined(this.wmsTexture) || !isDefined(quad) || !this.wmsMapEnabled) {
      return;
    }
    renderer.render(quad, camera);
  }
}
